/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { CountryCode } from '@common/components/dialog-update-credit-card/countries';
import { RouterReducerState, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Action } from '@ngrx/store';
import { AuthConfig } from '@shared/types/auth';
import { BillingCard } from '@shared/types/billing';
import { Cluster2xConnectionToken, ClusterId, ClusterInfo } from '@shared/types/cluster';
import {
  Cluster3xConfiguration,
  Cluster3xRestApiUrl,
  Cluster3xTopology,
  ClusterConnectors,
} from '@shared/types/cluster3x';
import { CacheGroupName, CacheName, DashboardGroup, IgniteCache, User } from '@shared/types/common';
import { Cluster3xDistributionZone } from '@shared/types/distribution-zone';
import { GGCCLicenseStatus } from '@shared/types/license';
import { MetricDefinition, MetricId } from '@shared/types/metrics';
import { DashboardTemplateAny } from '@shared/types/monitoring';
import { NebulaSubscriptionInfo } from '@shared/types/nebula';
import { NodeBulkSelector, NodeConsistentId, TopologySnapshot } from '@shared/types/node';
import { Cluster3xLocalPartitionStates } from '@shared/types/partition-states';
import { DurationInMs } from '@shared/types/snapshots';
import { Cluster3xTable } from '@shared/types/tables';
import { Team } from '@shared/types/team';
import { IOutputAreaSizes } from 'angular-split';
import { FrontendClusterUser } from './cluster-users';
import { Widget } from './widgets';

export * from './query-widgets';
export * from './widgets';

export interface AppState {
  loading: Map<string, number>;
  user: User | null;
  snackbarDuration: {
    error: DurationInMs;
    notification: DurationInMs;
  };
  errorActions: { time: Date; action: Action }[];
  clusters: ClusterInfo[];
  currentCluster: string | null;
  dashboardGroups: Record<string, DashboardGroup>;
  dashboards: Record<string, Dashboard>;
  dashboardTemplates: DashboardTemplate[];
  lastOpenedDashboard: Record<string, string>;
  router: RouterReducerState<SerializedRouterStateSnapshot>;
  clusterTopology: Map<string, TopologySnapshot>;
  cluster3xTopology: Map<string, Cluster3xTopology>;
  cluster3xConfiguration: Map<string, Cluster3xConfiguration>;
  clustersLoaded: boolean;
  blockNotifications: boolean;
  theme: Themes | null;
  caches: Map<string, IgniteCache[]>;
  metricDefs: Record<ClusterId, Record<MetricId, MetricDefinition>>;
  closedBanners: string[];
  sortModels: Record<string, SortModel[]>;
  sizeModels: Record<string, SizeModel[]>;
  splitAreaSizeModels: Record<string, SplitAreaSizeModels>;
  columnVisibilityModels: Record<string, ColumnVisibilityModel[]>;
  licenseStatus: GGCCLicenseStatus | null;
  authConfig: AuthConfig | null;
  teams: Team[] | null;
  nebulaSubscription: NebulaSubscriptionInfo | null;
  filterPanels: {
    [name: string]: {
      hasData: boolean;
      visible: boolean;
      type: 'manual' | 'auto';
    };
  };
  billingCards: BillingCard[];
  clusterUsers: Map<ClusterId, FrontendClusterUser[]>;
  connectionToken: Cluster2xConnectionToken | Cluster3xRestApiUrl | null;
  target: GGCCTarget | undefined;
  completedTutorials: string[];
  cluster3xTables: Map<string, Cluster3xTable[]>;
  cluster3xDistributionZones: Map<string, Cluster3xDistributionZone[]>;
  cluster3xLocalPartitionStates: Map<string, Cluster3xLocalPartitionStates>;
  clusterConnectors: ClusterConnectors;
}

export enum NodeIdentifier {
  NODE_ID = 'NODE_ID',
  NODE_CONSISTENT_ID = 'NODE_CONSISTENT_ID',
  NODE_NAME = 'NODE_NAME',
}

export type DashboardChartRangeFromNow = number;
export type DashboardChartRangeStartEnd = [startInMs: number, endInMs: number];
export type DashboardChartRange = DashboardChartRangeFromNow | DashboardChartRangeStartEnd;

export type DashboardMetadata = {
  title: string;
  baseTemplateId?: DashboardTemplateId;
  chartsRange: DashboardChartRange;
  nodeIdentifier?: NodeIdentifier;
  chartZoomRange?: DashboardChartRangeStartEnd;
  parametersPanelOpen?: boolean;
} & DashboardParameters;

export type DashboardParameters = {
  cacheNames?: CacheName[];
  nodeConsistentIds?: (NodeConsistentId | NodeBulkSelector.CLIENT | NodeBulkSelector.SERVER)[];
  cacheGroupNames?: CacheGroupName[];
};

export type DashboardIgniteBaseVersion = '2x' | '3x';

export interface Dashboard {
  id: string;
  metadata: DashboardMetadata;
  igniteBaseVersion?: DashboardIgniteBaseVersion;
  widgets: Widget[];
}

export type DashboardTemplateId = string;

export interface DashboardTemplate extends DashboardTemplateAny {
  workspace: Dashboard;
}

export type ExportedDashboardsDocument = {
  version: 1;
  dashboards: Dashboard[];
};

export type ExtractedErrorText = string & { _tag_: 'ExtractedErrorText' };

export interface GmcError {
  error: ExtractedErrorText;
  silent?: boolean;
}

export interface GmcStoreActionWithOptionalNotification {
  notification?: string;
}

export interface GmcNotificationAction {
  notification?: string;
}

export enum Themes {
  DARK = 'dark',
  LIGHT = 'light',
}

export interface Tab {
  id: string;
  name: string;
  title?: string;
  required?: boolean;
  loading?: boolean;
  hideMenu?: boolean;
}

export interface TabRouteData {
  isTab?: boolean;
}

export enum NodeBaselineChangeDeclineReasons {
  NODE_IS_ONLINE = 'NODE_IS_ONLINE',
  AUTO_ADJUST_ENABLED = 'AUTO_ADJUST_ENABLED',
  CLUSTER_IS_IN_MEMORY = 'CLUSTER_IS_IN_MEMORY',
  CLUSTER_IS_INACTIVE = 'CLUSTER_IS_INACTIVE',
}

export enum GmcErrorCodes {
  INVALID_QUERY_PARAMETER = 'Invalid sql query parameter',
}

export interface SortModel {
  colId: string;
  sort: FrontendSortDirection;
}

export type SizeModel = {
  colId: string;
  width?: number;
  flex: number | null;
};

export type ColumnVisibilityModel = {
  colId: string;
  hide: boolean;
};

export enum FrontendSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum BackendSortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum NodeType {
  CLIENT = 'client',
  SERVER = 'server',
  COORDINATOR = 'coordinator',
}

export type GGCCTarget = 'on-premise' | 'hosted';

export type ClipboardAllowedMimeTypes = 'text/html' | 'text/plain';

export enum OtherCountries {
  OTHER_EUROPE = 'OTEU',
  OTHER_ASIA = 'OTAS',
  OTHER_SOUTH_AMERICA = 'OTSA',
  OTHER_AFRICA = 'OTAF',
  REST_OF_THE_WORLD = 'ROTW',
}

export type CountryShortCode = CountryCode | OtherCountries;

export interface Country {
  label: string;
  value: string;
  code: string;
  shortCode: CountryShortCode;
}

export type SplitAreaSizeModels = IOutputAreaSizes;

export type TutorialStep<T extends {}> = T & {};

export interface Tutorial<T extends {}> {
  id: string;
  title: string;
  description: string;
  image: string;
  tutorial: TutorialStep<T>[];
  routes: string[];
  onStart?: () => Promise<void>;
}
